import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/use-toast';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { formatUtils } from '@/lib/utils';
import {
  KizeoTenantSyncState,
  TenantStatus,
  TenantSyncChangeKind,
  TenantSyncPreview,
} from '@activepieces/shared';

type SyncClientDialogProps = {
  clientId: string;
};

const tierLabel = (tier: string | null): string =>
  tier === null ? t('Unknown') : t(tier);

const statusLabel = (status: TenantStatus): string => {
  switch (status) {
    case TenantStatus.ACTIVE:
      return t('Active');
    case TenantStatus.INACTIVE:
      return t('Inactive');
    case TenantStatus.MIGRATING:
      return t('Migrating');
  }
};

const formatQuota = (quota: number | null): string =>
  quota === null ? t('Unknown') : quota.toLocaleString();

const DiffRow = ({
  label,
  current,
  next,
  destructive,
}: {
  label: string;
  current: string;
  next?: string | null;
  destructive?: boolean;
}) => {
  const changed = next != null && next !== current;
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2">
        {changed ? (
          <>
            <span className="text-muted-foreground line-through">
              {current}
            </span>
            <span className="text-muted-foreground">→</span>
            <span
              className={
                destructive
                  ? 'font-bold text-destructive'
                  : 'font-bold text-primary'
              }
            >
              {next}
            </span>
          </>
        ) : (
          <span className="font-semibold">{current}</span>
        )}
      </span>
    </div>
  );
};

const ChangeBox = ({ preview }: { preview: TenantSyncPreview }) => {
  const base = 'rounded-md border p-3 text-sm';
  switch (preview.changeKind) {
    case 'no_change':
      return (
        <div className={`${base} border-green-200 bg-green-50 text-green-800`}>
          <strong>{t('No change.')}</strong>{' '}
          {t('Corp and Khub are already aligned.')}
        </div>
      );
    case 'regular_update':
      return (
        <div className={`${base} border-green-200 bg-green-50 text-green-800`}>
          {t('No flow will be affected by this update.')}
        </div>
      );
    case 'plan_downgrade':
      return (
        <div className={`${base} border-amber-200 bg-amber-50 text-amber-800`}>
          <strong>{t('Downgrade to a lower plan.')}</strong>
          {preview.activeFlows > 0 && (
            <>
              {' '}
              {t(
                '{count, plural, one {The active flow will be disabled.} other {The # active flows will be disabled.}}',
                { count: preview.activeFlows },
              )}
            </>
          )}
        </div>
      );
    case 'access_lost':
      return (
        <div
          className={`${base} border-destructive/30 bg-destructive/5 text-destructive`}
        >
          <strong>{t('Access lost.')}</strong>{' '}
          {preview.activeFlows > 0
            ? t(
                '{count, plural, one {The active flow will be disabled and the client will be deactivated.} other {The # active flows will be disabled and the client will be deactivated.}}',
                { count: preview.activeFlows },
              )
            : t('The client will be deactivated.')}
        </div>
      );
    case 'corp_not_found':
      return (
        <div className={`${base} text-muted-foreground`}>
          {t('Client not found in Corp — no action will be applied.')}
        </div>
      );
  }
};

const confirmLabel = (changeKind: TenantSyncChangeKind): string => {
  switch (changeKind) {
    case 'plan_downgrade':
      return t('Confirm the downgrade');
    case 'access_lost':
      return t('Confirm the deactivation');
    case 'no_change':
    case 'corp_not_found':
      return t('Resync anyway');
    case 'regular_update':
      return t('Confirm');
  }
};

const PreviewBody = ({
  preview,
  isLoading,
  isError,
}: {
  preview: TenantSyncPreview | undefined;
  isLoading: boolean;
  isError: boolean;
}) => {
  if (isError) {
    return (
      <p className="text-destructive text-sm py-4">
        {t('Unable to load the sync preview. Close the dialog and retry.')}
      </p>
    );
  }
  if (isLoading || !preview) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }
  return (
    <div className="space-y-4 text-sm">
      <ChangeBox preview={preview} />

      <div className="divide-y rounded-md border">
        <DiffRow
          label={t('Plan')}
          current={tierLabel(preview.khubTier)}
          next={preview.corpTier === null ? null : tierLabel(preview.corpTier)}
        />
        <DiffRow
          label={t('Status')}
          current={statusLabel(preview.currentStatus)}
          next={statusLabel(preview.statusAfter)}
          destructive={preview.statusAfter === TenantStatus.INACTIVE}
        />
        <DiffRow
          label={t('Monthly quota')}
          current={formatQuota(preview.currentMonthlyQuota)}
          next={
            preview.newMonthlyQuota === null
              ? null
              : formatQuota(preview.newMonthlyQuota)
          }
        />
        <DiffRow
          label={t('Active flows')}
          current={String(preview.activeFlows)}
          next={String(preview.activeFlowsAfter)}
          destructive={preview.activeFlowsAfter < preview.activeFlows}
        />
      </div>
    </div>
  );
};

const LastSyncLine = ({
  state,
}: {
  state: KizeoTenantSyncState | null | undefined;
}) => {
  if (!state) {
    return (
      <span className="text-xs text-muted-foreground">{t('Never synced')}</span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span className="text-muted-foreground">{t('Last corp sync')}</span>
      <span className="font-bold text-foreground">
        {formatUtils.formatDateToAgo(new Date(state.lastSyncedAt))}
      </span>
      <Badge variant="secondary">{t(state.lastSyncTrigger)}</Badge>
    </span>
  );
};

const SyncClientDialog = ({ clientId }: SyncClientDialogProps) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: syncState } = useQuery({
    queryKey: ['admin-client-sync-state', clientId],
    queryFn: () => adminApi.getSyncState(clientId),
  });

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery({
    queryKey: ['admin-client-sync-preview', clientId],
    queryFn: () => adminApi.getSyncPreview(clientId),
    enabled: open,
  });

  const { mutate: runSync, isPending: isSyncing } = useMutation({
    mutationFn: () => adminApi.runSync(clientId),
    onSuccess: () => {
      setOpen(false);
      toast({
        variant: 'success',
        title: t('Client resynchronized with Corp'),
        description: t(
          'Active flows and plan information are now in sync with Corp.',
        ),
      });
      [
        ['admin-client', clientId],
        ['admin-client-policy', clientId],
        ['admin-client-usage', clientId],
        ['admin-client-sync-preview', clientId],
        ['admin-client-sync-state', clientId],
      ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    },
    onError: () => {
      toast({ title: t('Resynchronization failed'), variant: 'destructive' });
    },
  });

  return (
    <div className="flex flex-col gap-1.5">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('Sync with corp')}
          </Button>
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Resynchronize with Corp')}</DialogTitle>
            <DialogDescription>
              {t('Preview of the changes that will be applied from Corp.')}
            </DialogDescription>
          </DialogHeader>

          <PreviewBody
            preview={preview}
            isLoading={isPreviewLoading}
            isError={isPreviewError}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                {t('Cancel')}
              </Button>
            </DialogClose>
            {preview?.changeKind !== 'corp_not_found' && (
              <Button
                type="button"
                onClick={() => runSync()}
                loading={isSyncing}
                disabled={isPreviewLoading || !preview}
              >
                {preview ? confirmLabel(preview.changeKind) : t('Confirm')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LastSyncLine state={syncState} />
    </div>
  );
};

SyncClientDialog.displayName = 'SyncClientDialog';

export default SyncClientDialog;
